"use client";

import { useEffect, useState } from "react";

/**
 * Wraps Clerk's <PricingTable /> with a skeleton placeholder that
 * reserves the same vertical space, preventing CLS while Clerk JS
 * loads and hydrates. Once the Clerk component renders real DOM
 * nodes inside the container, the skeleton is hidden.
 */
export function PricingTableSkeleton({
  children,
}: {
  children: React.ReactNode;
}) {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // Clerk injects its pricing table into a shadow DOM / iframe.
    // We poll briefly until real content appears, then hide skeleton.
    const el = document.getElementById("pricing-table-container");
    if (!el) return;

    const observer = new MutationObserver(() => {
      if (el.children.length > 0) {
        // Clerk has rendered something — check it's not just our skeleton
        const hasClerkContent = el.querySelector("[data-clerk], iframe, [class*='cl-']");
        if (hasClerkContent) {
          setLoaded(true);
          observer.disconnect();
        }
      }
    });

    observer.observe(el, { childList: true, subtree: true });

    // Fallback: always hide skeleton after 4s even if detection fails
    const timeout = setTimeout(() => {
      setLoaded(true);
      observer.disconnect();
    }, 4000);

    return () => {
      observer.disconnect();
      clearTimeout(timeout);
    };
  }, []);

  return (
    <div className="relative min-h-[320px]">
      {/* Skeleton — two side-by-side plan cards */}
      {!loaded && (
        <div className="flex justify-center gap-6" aria-hidden="true">
          <SkeletonCard />
          <SkeletonCard highlighted />
        </div>
      )}

      {/* Clerk PricingTable renders here */}
      <div id="pricing-table-container" className={loaded ? "" : "opacity-0 absolute inset-0"}>
        {children}
      </div>
    </div>
  );
}

function SkeletonCard({ highlighted }: { highlighted?: boolean }) {
  return (
    <div
      className={`w-64 rounded-xl border p-6 space-y-4 animate-pulse ${
        highlighted
          ? "border-primary/30 bg-primary/5"
          : "border-input bg-muted/20"
      }`}
    >
      {/* Plan name */}
      <div className="h-5 w-16 rounded bg-muted/40" />
      {/* Price */}
      <div className="h-8 w-24 rounded bg-muted/40" />
      {/* Description */}
      <div className="space-y-2">
        <div className="h-3 w-full rounded bg-muted/30" />
        <div className="h-3 w-3/4 rounded bg-muted/30" />
      </div>
      {/* Button */}
      <div
        className={`h-10 w-full rounded-lg ${
          highlighted ? "bg-primary/20" : "bg-muted/30"
        }`}
      />
      {/* Feature list */}
      <div className="space-y-2 pt-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="size-4 rounded-full bg-muted/30" />
            <div className="h-3 flex-1 rounded bg-muted/30" />
          </div>
        ))}
      </div>
    </div>
  );
}
